'use client';

import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';

// Static data - defined outside component to avoid re-creation on renders
const frameworks = [
  { id: 1, name: 'NIST Cybersecurity Framework', fullName: 'NIST Cybersecurity Framework', x: 20, y: 15, size: 'large', type: 'primary', color: 'text-white', glow: 'shadow-white/60' },
  { id: 2, name: 'ISO 27001', fullName: 'ISO/IEC 27001:2022', x: 75, y: 20, size: 'large', type: 'primary', color: 'text-indigo-200', glow: 'shadow-indigo-400/80' },
  { id: 3, name: 'GDPR', fullName: 'General Data Protection Regulation', x: 10, y: 50, size: 'large', type: 'regional', color: 'text-indigo-300', glow: 'shadow-indigo-300/70' },
  { id: 4, name: 'CIS Controls', fullName: 'CIS Critical Security Controls v8', x: 50, y: 10, size: 'medium', type: 'primary', color: 'text-indigo-100', glow: 'shadow-indigo-200/60' },
  { id: 5, name: 'NIST 800-53', fullName: 'NIST SP 800-53 Rev. 5', x: 85, y: 45, size: 'medium', type: 'primary', color: 'text-indigo-400', glow: 'shadow-indigo-400/75' },
  { id: 6, name: 'DORA', fullName: 'Digital Operational Resilience Act', x: 40, y: 35, size: 'medium', type: 'regional', color: 'text-blue-300', glow: 'shadow-blue-300/65' },
  { id: 7, name: 'PCI-DSS', fullName: 'Payment Card Industry Data Security Standard', x: 70, y: 65, size: 'medium', type: 'industry', color: 'text-violet-300', glow: 'shadow-violet-300/65' },
  { id: 8, name: 'HIPAA', fullName: 'Health Insurance Portability and Accountability Act', x: 25, y: 70, size: 'medium', type: 'industry', color: 'text-indigo-200', glow: 'shadow-indigo-200/60' },
  { id: 9, name: 'NIS2', fullName: 'Network and Information Systems Directive 2', x: 90, y: 75, size: 'small', type: 'regional', color: 'text-indigo-300', glow: 'shadow-indigo-300/50' },
  { id: 10, name: 'SOC 2', fullName: 'Service Organization Control 2', x: 55, y: 80, size: 'small', type: 'primary', color: 'text-purple-300', glow: 'shadow-purple-300/55' },
];

const connections = [
  { from: 1, to: 2, relationship: 'Cybersecurity alignment' }, // NIST CSF to ISO 27001
  { from: 1, to: 4, relationship: 'Control mapping' }, // NIST CSF to CIS Controls  
  { from: 1, to: 5, relationship: 'Implementation guidance' }, // NIST CSF to NIST 800-53
  { from: 2, to: 6, relationship: 'Risk management' }, // ISO 27001 to DORA
  { from: 3, to: 6, relationship: 'Data protection' }, // GDPR to DORA
  { from: 3, to: 9, relationship: 'EU regulation family' }, // GDPR to NIS2
  { from: 7, to: 8, relationship: 'Data security standards' }, // PCI-DSS to HIPAA
  { from: 4, to: 5, relationship: 'NIST framework family' }, // CIS Controls to NIST 800-53
];

// Utility function to shuffle array
const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const ComplianceConstellation = ({ isAnalyzing = false }) => {
  const [activeConnections, setActiveConnections] = useState([]);
  const [rotationAngle, setRotationAngle] = useState(0);
  const [hoveredConnection, setHoveredConnection] = useState(null);
  const [visibleLabels, setVisibleLabels] = useState([]);
  const [particles, setParticles] = useState([]);
  const [shuffledConnections, setShuffledConnections] = useState([]);
  
  // Rotation configuration
  const rotationSteps = 5; // Number of stops to complete 360°
  const rotationInterval = 5000; // 5 seconds between rotations
  const anglePerStep = 360 / rotationSteps; // 72° per step

  // Initialize shuffled connections on client side
  useEffect(() => {
    setShuffledConnections(shuffleArray(connections));
  }, []);

  // Animate connections sequentially - only when analyzing
  useEffect(() => {
    if (shuffledConnections.length === 0 || !isAnalyzing) return;

    const animateConnections = () => {
      shuffledConnections.forEach((_, index) => {
        setTimeout(() => {
          setActiveConnections(prev => [...prev, index]);
        }, index * 800 + 2000); // Start after 2s, then 800ms intervals
      });
    };

    const timer = setTimeout(animateConnections, 1000);
    
    // Reset and restart animation with new shuffle
    const resetTimer = setInterval(() => {
      setActiveConnections([]);
      setShuffledConnections(shuffleArray(connections));
      setTimeout(animateConnections, 1000);
    }, 8000);

    return () => {
      clearTimeout(timer);
      clearInterval(resetTimer);
    };
  }, [shuffledConnections, isAnalyzing]);

  // Handle constellation rotation - only when analyzing
  useEffect(() => {
    if (!isAnalyzing) return;

    const rotationTimer = setInterval(() => {
      setRotationAngle(prev => {
        const nextAngle = prev + anglePerStep;
        // Reset to 0 after completing full rotation to avoid large numbers
        return nextAngle >= 360 ? 0 : nextAngle;
      });
    }, rotationInterval);

    return () => clearInterval(rotationTimer);
  }, [anglePerStep, rotationInterval, isAnalyzing]);

  // Handle automatic label display - only when analyzing
  useEffect(() => {
    if (!isAnalyzing) return;

    let currentIndex = 0;
    
    const showNextLabels = () => {
      // Show 1-2 random frameworks
      const numLabelsToShow = Math.random() > 0.5 ? 2 : 1;
      const selectedIds = [];
      
      for (let i = 0; i < numLabelsToShow; i++) {
        selectedIds.push(frameworks[(currentIndex + i) % frameworks.length].id);
      }
      
      // Set new labels (fade-in will start immediately)
      setVisibleLabels(selectedIds);
      currentIndex = (currentIndex + numLabelsToShow) % frameworks.length;
      
      // Clear labels after a brief moment to create smooth transition
      setTimeout(() => {
        setVisibleLabels([]);
      }, 4200); // Clear 800ms before next cycle for smooth overlap
    };

    // Start showing labels after initial animation
    const initialTimer = setTimeout(showNextLabels, 3000);
    
    // Cycle through labels every 5 seconds
    const cycleTimer = setInterval(showNextLabels, 5000);

    return () => {
      clearTimeout(initialTimer);
      clearInterval(cycleTimer);
    };
  }, [isAnalyzing]);

  // Reset animations when not analyzing
  useEffect(() => {
    if (!isAnalyzing) {
      setActiveConnections([]);
      setVisibleLabels([]);
      setRotationAngle(0);
    }
  }, [isAnalyzing]);

  // Generate particle positions on client side only to avoid hydration mismatch
  useEffect(() => {
    const generateParticles = () => {
      const newParticles = [];
      for (let i = 0; i < 20; i++) {
        newParticles.push({
          left: Math.random() * 100,
          top: Math.random() * 100,
        });
      }
      setParticles(newParticles);
    };

    generateParticles();
  }, []);

  const getStarSize = (size) => {
    switch (size) {
      case 'large': return 'w-3 h-3';
      case 'medium': return 'w-2 h-2';
      case 'small': return 'w-1.5 h-1.5';
      default: return 'w-2 h-2';
    }
  };

  return (
    <div className="relative w-full h-96 bg-gradient-to-br from-gray-900/95 to-black/90 rounded-xl overflow-hidden border border-gray-700/30">
      {/* Animated background particles */}
      <div className="absolute inset-0">
        {particles.map((particle, i) => (
          <motion.div
            key={i}
            className="absolute w-px h-px bg-white/20 rounded-full"
            style={{
              left: particle.left + '%',
              top: particle.top + '%',
            }}
            animate={{
              opacity: [0.2, 0.8, 0.2],
              scale: [1, 1.5, 1],
            }}
            transition={{
              duration: 3 + (i * 0.1) % 2, // Use index-based variation instead of Math.random()
              repeat: Infinity,
              delay: (i * 0.2) % 2, // Use index-based delay instead of Math.random()
            }}
          />
        ))}
      </div>

      {/* Central scanning pulse - stays fixed */}
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20">
        <motion.div
          className="w-2 h-2 bg-purple-400/60 rounded-full"
          animate={{
            scale: [1, 8, 1],
            opacity: [0.8, 0, 0.8],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'easeOut',
          }}
        />
      </div>

      {/* Rotating constellation container */}
      <motion.div
        className="absolute inset-0 w-full h-full"
        animate={{ rotate: rotationAngle }}
        transition={{ 
          duration: 2, 
          ease: 'easeInOut'
        }}
      >
        {/* Framework connections */}
        <svg className="absolute inset-0 w-full h-full">
        {shuffledConnections.map((connection, index) => {
          const fromFramework = frameworks.find(f => f.id === connection.from);
          const toFramework = frameworks.find(f => f.id === connection.to);
          
          if (!fromFramework || !toFramework) return null;

          const isActive = activeConnections.includes(index);
          const isHovered = hoveredConnection === index;
          
          return (
            <g key={`${connection.from}-${connection.to}-${index}`}>
              <motion.line
                x1={`${fromFramework.x}%`}
                y1={`${fromFramework.y}%`}
                x2={`${toFramework.x}%`}
                y2={`${toFramework.y}%`}
                stroke="url(#connectionGradient)"
                strokeWidth={isActive ? (isHovered ? "3" : "2") : "1"}
                strokeDasharray="4 4"
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ 
                  pathLength: isActive ? 1 : 0,
                  opacity: isActive ? (isHovered ? 0.9 : 0.6) : 0
                }}
                transition={{ duration: 1, ease: 'easeInOut' }}
                style={{ cursor: isActive ? 'pointer' : 'default' }}
                onMouseEnter={() => isActive && setHoveredConnection(index)}
                onMouseLeave={() => setHoveredConnection(null)}
              />
              
              {/* Invisible thicker line for easier hover detection */}
              {isActive && (
                <line
                  x1={`${fromFramework.x}%`}
                  y1={`${fromFramework.y}%`}
                  x2={`${toFramework.x}%`}
                  y2={`${toFramework.y}%`}
                  stroke="transparent"
                  strokeWidth="10"
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={() => setHoveredConnection(index)}
                  onMouseLeave={() => setHoveredConnection(null)}
                />
              )}
            </g>
          );
        })}
        
        {/* Gradient definition for connections */}
        <defs>
          <linearGradient id="connectionGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#a855f7" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.4" />
          </linearGradient>
        </defs>
      </svg>

      {/* Framework stars */}
      {frameworks.map((framework, index) => (
        <motion.div
          key={framework.id}
          className="absolute transform -translate-x-1/2 -translate-y-1/2"
          style={{ left: `${framework.x}%`, top: `${framework.y}%` }}
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ 
            delay: index * 0.3,
            duration: 0.6,
            type: 'spring',
            stiffness: 200 
          }}
        >
          {/* Star glow effect */}
          <div className={`${getStarSize(framework.size)} ${framework.color} rounded-full bg-current relative shadow-lg ${framework.glow} transition-all duration-300 group-hover:scale-110 group-hover:shadow-xl`}>
            {/* Pulsing animation for emerging frameworks */}
            {framework.type === 'emerging' && (
              <motion.div
                className="absolute inset-0 rounded-full bg-current"
                animate={{ scale: [1, 1.5, 1], opacity: [0.8, 0, 0.8] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
            )}
          </div>
        </motion.div>
      ))}
      </motion.div>

      {/* Static framework labels - follow rotating stars but don't rotate */}
      {visibleLabels.map(frameworkId => {
        const framework = frameworks.find(f => f.id === frameworkId);
        if (!framework) return null;

        // Calculate rotated position of the star
        const radians = (rotationAngle * Math.PI) / 180;
        const centerX = 50;
        const centerY = 50;
        
        // Original star position
        const relX = framework.x - centerX;
        const relY = framework.y - centerY;
        
        // Rotated position
        const rotatedX = centerX + (relX * Math.cos(radians) - relY * Math.sin(radians));
        const rotatedY = centerY + (relX * Math.sin(radians) + relY * Math.cos(radians));

        return (
          <motion.div
            key={`label-${framework.id}`}
            className="absolute pointer-events-none z-40"
            style={{
              left: `${rotatedX}%`,
              top: `${rotatedY - 8}%`, // Offset above the star
              transform: 'translate(-50%, -100%)'
            }}
            initial={{ opacity: 0, scale: 0.95, y: 5 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 5 }}
            transition={{ duration: 1.2, ease: 'easeInOut' }}
          >
            <div className="bg-gray-900/95 backdrop-blur-sm text-white text-xs px-3 py-2 rounded-lg border border-purple-400/50 shadow-xl shadow-purple-400/20 whitespace-nowrap">
              <div className="font-semibold text-purple-300">{framework.name}</div>
              {framework.fullName !== framework.name && (
                <div className="text-gray-300 text-xs mt-1">{framework.fullName}</div>
              )}
            </div>
          </motion.div>
        );
      })}

      {/* Connection relationship tooltip */}
      {hoveredConnection !== null && shuffledConnections[hoveredConnection] && (
        <motion.div
          className="absolute pointer-events-none z-40 bg-black/90 backdrop-blur-sm text-white text-xs px-3 py-2 rounded-lg border border-purple-500/40"
          style={{
            left: '50%',
            top: '10%',
            transform: 'translateX(-50%)',
          }}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
        >
          <div className="text-center">
            <div className="text-purple-300 font-medium">
              {shuffledConnections[hoveredConnection].relationship}
            </div>
            <div className="text-gray-400 text-xs mt-1">
              {frameworks.find(f => f.id === shuffledConnections[hoveredConnection].from)?.name} ↔{' '}
              {frameworks.find(f => f.id === shuffledConnections[hoveredConnection].to)?.name}
            </div>
          </div>
        </motion.div>
      )}

      {/* Discovery status indicator */}
      <div className="absolute bottom-4 left-4">
        <motion.div
          className="flex items-center space-x-2 text-xs text-gray-400"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
        >
          <div className={`w-2 h-2 bg-purple-400 rounded-full ${isAnalyzing ? 'animate-pulse' : ''}`}></div>
          <span>
            {isAnalyzing ? 'Discovering compliance requirements...' : 'Ready for compliance discovery'}
          </span>
        </motion.div>
      </div>

      {/* Framework count */}
      <div className="absolute top-4 right-4">
        <motion.div
          className="text-purple-400 text-sm font-semibold"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2 }}
        >
          {/*{frameworks.length} frameworks analyzed*/}
        </motion.div>
      </div>
    </div>
  );
};

export default ComplianceConstellation; 